import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { TaskForm } from '../components/tasks/TaskForm';
import { useStore } from '../store/useStore';
import { Department } from '../types';
import { departmentList } from '../utils/departments';
import { getDepartmentLabel } from '../utils/departments';
import { 
  Building2, 
  Users, 
  CheckCircle, 
  Clock,
  ListTodo,
  TrendingUp
} from 'lucide-react';
import { motion } from 'framer-motion';

export const DepartmentsPage: React.FC = () => {
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const { currentUser, tasks, availableUsers } = useStore();

  const getDepartmentStats = (dept: Department) => {
    const deptTasks = tasks.filter(task => task.department === dept);
    const employees = availableUsers.filter(user => user.department === dept);
    const completedTasks = deptTasks.filter(task => task.status === 'COMPLETED');
    const inProgressTasks = deptTasks.filter(task => task.status === 'IN_PROGRESS');
    
    return {
      totalTasks: deptTasks.length,
      completedTasks: completedTasks.length,
      inProgressTasks: inProgressTasks.length,
      employeeCount: employees.length,
      managers: employees.filter(emp => emp.role === 'MANAGER'),
      completionRate: deptTasks.length > 0 
        ? Math.round((completedTasks.length / deptTasks.length) * 100)
        : 0
    };
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Department Overview</h1>
        <p className="text-gray-600">Monitor performance and create tasks across departments</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {departmentList.map((dept) => {
          const stats = getDepartmentStats(dept);
          const progress = stats.completionRate;

          return (
            <motion.div
              key={dept}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="p-6 h-full hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
                      <Building2 className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{getDepartmentLabel(dept)}</h2>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{stats.employeeCount} team members</span>
                      </div>
                    </div>
                  </div>
                  {currentUser && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setSelectedDept(dept)}
                      icon={<ListTodo className="w-4 h-4" />}
                    > 
                      Create Task
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">In Progress</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{stats.inProgressTasks}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">Completed</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-900">{stats.completedTasks}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Task Progress</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {stats.managers.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex -space-x-2">
                      {stats.managers.map((manager) => (
                        <img
                          key={manager.id}
                          src={manager.avatar}
                          alt={manager.name}
                          title={manager.name}
                          className="w-8 h-8 rounded-full border-2 border-white"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>

      {selectedDept && (
        <div className="mt-8">
          <TaskForm
            department={selectedDept}
            onSuccess={() => setSelectedDept(null)}
          />
        </div>
      )}
    </div>
  );
};